import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';

/**
 * Hook to manage PWA push notifications (works on iOS 16.4+ when installed as PWA)
 */
export const usePushNotifications = (userId) => {
    const [permission, setPermission] = useState('default');
    const [isSubscribed, setIsSubscribed] = useState(false);
    const [isPWA, setIsPWA] = useState(false);
    const [isIOS, setIsIOS] = useState(false);
    const [vapidKey, setVapidKey] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    // Check if we're running as a PWA
    useEffect(() => {
        const checkPWA = () => {
            const isStandalone = window.matchMedia('(display-mode: standalone)').matches
                || window.navigator.standalone === true;
            setIsPWA(isStandalone);
        };

        const checkIOS = () => {
            const userAgent = window.navigator.userAgent.toLowerCase();
            setIsIOS(/iphone|ipad|ipod/.test(userAgent));
        };

        checkPWA();
        checkIOS();

        // Check notification permission
        if ('Notification' in window) {
            setPermission(Notification.permission);
        }
    }, []);

    // Fetch VAPID public key from server
    useEffect(() => {
        const fetchVapidKey = async () => {
            try {
                // Try env variable first (for local dev)
                const envKey = process.env.REACT_APP_VAPID_PUBLIC_KEY;
                if (envKey) {
                    setVapidKey(envKey);
                    return;
                }

                // Fetch from API
                const response = await fetch('/api/push/vapid-key');
                if (response.ok) {
                    const data = await response.json();
                    setVapidKey(data.vapidPublicKey);
                }
            } catch (error) {
                console.log('VAPID key not available - push notifications disabled');
            }
        };

        fetchVapidKey();
    }, []);

    // Check existing subscription
    useEffect(() => {
        const checkSubscription = async () => {
            if (!('serviceWorker' in navigator) || !('PushManager' in window)) return;

            try {
                const registration = await navigator.serviceWorker.ready;
                const subscription = await registration.pushManager.getSubscription();
                setIsSubscribed(!!subscription);
            } catch (error) {
                console.error('Error checking subscription:', error);
            }
        };

        checkSubscription();
    }, []);

    // Convert VAPID key to Uint8Array
    const urlBase64ToUint8Array = (base64String) => {
        const padding = '='.repeat((4 - base64String.length % 4) % 4);
        const base64 = (base64String + padding)
            .replace(/-/g, '+')
            .replace(/_/g, '/');

        const rawData = window.atob(base64);
        const outputArray = new Uint8Array(rawData.length);

        for (let i = 0; i < rawData.length; ++i) {
            outputArray[i] = rawData.charCodeAt(i);
        }
        return outputArray;
    };

    // Subscribe to push notifications
    const subscribe = useCallback(async () => {
        if (!userId) {
            toast.error('Devi essere loggato per ricevere notifiche');
            return false;
        }

        if (!vapidKey) {
            toast.error('Notifiche push non configurate sul server');
            return false;
        }

        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            toast.error('Il tuo browser non supporta le notifiche push');
            return false;
        }

        setIsLoading(true);

        try {
            // Request permission
            const permissionResult = await Notification.requestPermission();
            setPermission(permissionResult);

            if (permissionResult !== 'granted') {
                toast.warning('Permesso notifiche negato', {
                    description: 'Il Don non potrà contattarti...'
                });
                setIsLoading(false);
                return false;
            }

            // Get service worker registration
            const registration = await navigator.serviceWorker.ready;

            // Subscribe to push
            const subscription = await registration.pushManager.subscribe({
                userVisibleOnly: true,
                applicationServerKey: urlBase64ToUint8Array(vapidKey)
            });

            // Get subscription keys
            const subscriptionJson = subscription.toJSON();
            const keys = subscriptionJson.keys;

            // Detect device type
            let deviceType = 'desktop';
            if (isIOS && isPWA) {
                deviceType = 'ios_pwa';
            } else if (isIOS) {
                deviceType = 'ios_safari';
            } else if (/android/i.test(navigator.userAgent)) {
                deviceType = 'android';
            }

            // Send subscription to backend
            const response = await fetch('/api/push/subscribe', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    endpoint: subscription.endpoint,
                    p256dh: keys.p256dh,
                    auth: keys.auth,
                    user_id: userId,
                    device_type: deviceType
                })
            });

            if (response.ok) {
                setIsSubscribed(true);
                toast.success('Notifiche attivate! 🔔', {
                    description: 'Ora riceverai ordini dal Don anche quando l\'app è chiusa.'
                });
                setIsLoading(false);
                return true;
            } else {
                throw new Error('Failed to save subscription');
            }
        } catch (error) {
            console.error('Subscribe error:', error);
            toast.error('Errore attivazione notifiche', {
                description: error.message
            });
            setIsLoading(false);
            return false;
        }
    }, [userId, vapidKey, isIOS, isPWA]);

    // Unsubscribe from push notifications
    const unsubscribe = useCallback(async () => {
        if (!('serviceWorker' in navigator)) return false;

        try {
            const registration = await navigator.serviceWorker.ready;
            const subscription = await registration.pushManager.getSubscription();

            if (subscription) {
                await subscription.unsubscribe();

                // Tell backend
                await fetch(`/api/push/unsubscribe?endpoint=${encodeURIComponent(subscription.endpoint)}&user_id=${userId}`, {
                    method: 'DELETE'
                });
            }

            setIsSubscribed(false);
            toast.info('Notifiche disattivate');
            return true;
        } catch (error) {
            console.error('Unsubscribe error:', error);
            return false;
        }
    }, [userId]);

    // Test push notification
    const testPush = useCallback(async () => {
        if (!userId) return false;

        try {
            const response = await fetch(`/api/push/test/${userId}`, {
                method: 'POST'
            });
            const data = await response.json();

            if (data.success) {
                toast.success('Notifica test inviata!');
            } else {
                // If backend says "no subscription" but we think we are subscribed, try to resync
                console.log("Test failed - attempting auto-resync...");
                toast.loading("Risincronizzazione notifiche...", { duration: 2000 });
                const resyncSuccess = await subscribe();

                if (resyncSuccess) {
                    toast.success("Notifiche ripristinate! Riprova il test.");
                } else {
                    toast.warning('Nessuna sottoscrizione trovata. Riprova ad attivare.');
                }
            }
            return data.success;
        } catch (error) {
            console.error('Test push error:', error);
            return false;
        }
    }, [userId, subscribe]);

    return {
        permission,
        isSubscribed,
        isPWA,
        isIOS,
        isLoading,
        isSupported: 'PushManager' in window && 'serviceWorker' in navigator,
        needsPWAInstall: isIOS && !isPWA,
        subscribe,
        unsubscribe,
        testPush
    };
};
